import { useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useForm, yupResolver } from '@mantine/form';
import { Flex, Space, Text } from '@mantine/core';
import { useLocation, useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { PasswordInput, TextInput, Link } from '@common/components';
import { AuthFormHoc } from 'src/hoc';
import { ILoginValues } from '@common/types/types';
import { PATHS } from 'src/constants';
import { setIsAuthorizeAction, useAppDispatch } from 'src/store';
import { withTranslation, validationPattern } from '@common/helpers';
import { useFormFocus } from '@common/hooks';
import styles  from './styles.module.scss';
const initialValues: ILoginValues = {
  email: '',
  password: '',
};

const validationFormSchema = yup.object().shape({
  email: validationPattern.email,
  password: yup.string().required(withTranslation('auth', 'passwordRequired')),
});

const LoginForm = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { firstLogin } = location.state || {};
  const [t] = useTranslation('auth');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const form = useForm({
    initialValues: initialValues,
    validate: yupResolver(validationFormSchema),
  });
  const { setNeedFocus } = useFormFocus({ formErrors: form.errors });
  const [systemError, setSystemError] = useState<null | string>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localStorage.removeItem('personalForm');
    localStorage.removeItem('isPersonalInfoFormEnabled');

    if (systemError) {
      errorRef.current?.setAttribute('tabindex', '0');
      errorRef.current?.focus();
    }
  }, [systemError]);

  const onErrorHandler = (): void => {
    setNeedFocus(true);
    setSystemError(null);
  };

  const onSubmitHandler = async (values: ILoginValues): Promise<void> => {
    setNeedFocus(true);
    setIsLoading(true);
    setSystemError(null);
    try {
      await Auth.signIn(values.email, values.password);
      if (firstLogin) {
        navigate(PATHS.ONBOARDING);
      } else {
        dispatch(setIsAuthorizeAction(true));
        navigate(PATHS.MAIN);
      }
    } catch (error: any) {
      if (error.code === 'UserNotConfirmedException') {
        navigate(PATHS.SEND_VERIFY_LINK, {
          state: {
            email: values.email,
            isRecover: false,
          },
        });
      } else if (error.code === 'UserNotFoundException') {
        setSystemError(`${t('userNotFoundExceptionLogin')}`);
      } else if (error instanceof Error) {
        setSystemError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthFormHoc
      title={t('login')}
      description={
        <p>Welcome back! Please enter your details. </p>
        // <>
        //   <p>{t('doNotHaveAccount')}</p>
        //   <Link navigateTo={PATHS.SIGN_UP}>{t('signUp')}</Link>
        // </>
      }
      form={form}
      submitText={`${t('loginbtn')}`}
      onSubmitHandler={onSubmitHandler}
      onErrorHandler={onErrorHandler}
      isLoading={isLoading}
      classContainer={styles.loginFormContaine}
      >
      <>
        {systemError && (
          <Flex ref={errorRef}>
            <Text color={'#a80000'}>{systemError}</Text>
          </Flex>
        )}
        <TextInput
          withAsterisk
          label={`${t('email')}`}
          placeholder={`${t('enterYour')} ${t('email')}`}
          ariaLabel={`${t('emailDescribeLoginPage')}`}
          {...form.getInputProps('email')}
          required
        />
        <Space h={'md'} />
        <PasswordInput
          withAsterisk
          label={t('password')}
          placeholder={`${t('enter')} ${t('password')}`}
          ariaLabel={`${t('passwordDescribeLoginPage')}`}
          {...form.getInputProps('password')}
          required
        />
        <Space h={'md'} />
        <Link navigateTo={PATHS.FORGOT_PASSWORD}>{t('forgotPassword')}</Link>
        {/* <br></br>  */}
      </>
    </AuthFormHoc>
  );
};

export default LoginForm;
